import { Box, ButtonGroup, Flex, Text, useTheme } from "@chakra-ui/react";
import React, { useMemo, useState } from "react";
import { FiExternalLink } from "react-icons/fi";
import { HiOutlineBell, HiOutlineBellSlash } from "react-icons/hi2";
import { IoPeopleSharp } from "react-icons/io5";
import { useNavigate } from "react-router-dom";

import { Button, EditableText, PageTitle, useToast } from "../../../components";
import { AtsIcon } from "../../../components/Icons/AtsIcon";
import { buildAshbyLink } from "../../../utils/ashby";
import { buildGemPositionLink } from "../../../utils/gem";
import { buildLeverLink } from "../../../utils/lever";
import { useCandidateAlertDisabled } from "../../components/CandidateAlert/useCandidateAlertDisabled";
import {
  Ats,
  CandidateAlertUserSubscriptionsQuery,
  Client,
  Position,
  useAddCandidateAlertUserSubscriptionsMutation,
  useCandidateAlertUserSubscriptionsQuery,
  useMuteCandidateAlertUserSubscriptionsMutation,
} from "../../graphql";
import useLink from "../../hooks/useLink";

interface PositionHeaderProps {
  position: Pick<
    Position,
    | "id"
    | "title"
    | "requisitionId"
    | "greenhouseLink"
    | "leverPostingId"
    | "customAtsUrl"
    | "smartrecruitersLink"
    | "mergeType"
    | "mergeRemoteId"
  > & { client: Pick<Client, "name"> };
  canEdit: boolean;
  loading: boolean;
  handleSubmitTitle: (value: string) => void;
  handleSubmitRequisitionId: (value: string) => void;
}

type PositionSubscription =
  CandidateAlertUserSubscriptionsQuery["candidateAlertUserSubscriptions"]["positionSubscriptions"][0];

const PositionHeader: React.FC<PositionHeaderProps> = ({
  position,
  canEdit,
  loading,
  handleSubmitTitle,
  handleSubmitRequisitionId,
}) => {
  const navigate = useNavigate();
  const hiringTeamLink = useLink({
    type: "hiringTeam",
    positionId: position.id,
  });
  const theme = useTheme();
  const toast = useToast();
  const [hasReqId, setHasReqId] = useState(!!position.requisitionId);
  const { colors } = theme;
  const candidateAlertsDisabled = useCandidateAlertDisabled();

  const [currentAlertSubscription, setCurrentAlertSubscription] = useState<
    PositionSubscription | undefined
  >();
  const { data: candidateAlertSubscriptions, refetch } =
    useCandidateAlertUserSubscriptionsQuery();
  useMemo(() => {
    setCurrentAlertSubscription(
      candidateAlertSubscriptions?.candidateAlertUserSubscriptions.positionSubscriptions.find(
        (item) => {
          if (item.positionId === position.id) return true;
          return false;
        }
      )
    );
  }, [candidateAlertSubscriptions]);
  const [addSubscription, { loading: addLoading }] =
    useAddCandidateAlertUserSubscriptionsMutation({
      onCompleted: (data) => {
        if (data.addCandidateAlertUserSubscriptions) {
          toast({
            title: "Success",
            description:
              "You have subscribed to all future candidate alerts for this position",
            status: "success",
          });
          refetch();
        }
      },
    });
  const [muteSubscription, { loading: muteLoading }] =
    useMuteCandidateAlertUserSubscriptionsMutation({
      onCompleted: (data) => {
        if (data.muteCandidateAlertUserSubscriptions) {
          toast({
            title: "Success",
            description:
              "You have muted all future candidate alerts for this position",
            status: "success",
          });
          refetch();
        }
      },
    });
  const subscriptionLoading = addLoading || muteLoading;

  let atsName = null;
  let href = null;
  if (position.leverPostingId) {
    atsName = "Lever";
    href = buildLeverLink(position.leverPostingId);
  }
  if (position.greenhouseLink) {
    atsName = "Greenhouse";
    href = position.greenhouseLink;
  }
  if (position.mergeType === Ats.Ashby && !!position.mergeRemoteId) {
    atsName = "Ashby";
    href = buildAshbyLink(position.mergeRemoteId);
  }
  if (position.smartrecruitersLink) {
    atsName = "SmartRecruiters";
    href = position.smartrecruitersLink;
  }
  if (position.mergeType === Ats.Gem && !!position.mergeRemoteId) {
    atsName = "Gem";
    href = buildGemPositionLink(position.mergeRemoteId);
  }

  return (
    <Flex
      direction={["column", "column", "row"]}
      align="start"
      justify="space-between"
    >
      <Box flex="1">
        <PageTitle
          titleText={position.title}
          secondaryText={position.client.name}
          editable={canEdit && !loading}
          onTitleSubmit={handleSubmitTitle}
          mb={[2, 2, 4]}
        />
        <Flex align="center" mb={[4, 4, 10]} ml={0}>
          <Text fontSize="sm" color="gray.500" mr={2} whiteSpace="nowrap">
            Req ID:
          </Text>
          <EditableText
            defaultValue={position.requisitionId || ""}
            onSubmit={(value) => {
              setHasReqId(!!value.trim());
              handleSubmitRequisitionId(value);
            }}
            maxLength={100}
            isDisabled={!canEdit || loading}
            showIcon="hover"
            placeholder="Add requisition ID"
            fontSize="sm"
            previewProps={{ color: hasReqId ? undefined : "gray.400" }}
          />
        </Flex>
      </Box>
      <ButtonGroup spacing="2" size="sm" variant="ghost" mb={[10, 10, 0]}>
        {!!atsName && href && (
          <Button
            as="a"
            href={href}
            target="_blank"
            leftIcon={
              <AtsIcon
                fill={colors.blue[600]}
                marginRight="0"
                isGreenhouse={atsName === "Greenhouse"}
                isLever={atsName === "Lever"}
                isAshby={atsName === "Ashby"}
                isGem={atsName === "Gem"}
                isSmartrecruiters={atsName === "SmartRecruiters"}
              />
            }
            colorScheme="blue"
            aria-label={atsName}
            borderRadius="base"
          >
            View in {atsName}
          </Button>
        )}
        {!candidateAlertsDisabled &&
          (currentAlertSubscription?.mutedAt ||
            currentAlertSubscription === undefined) && (
            <Button
              colorScheme="blue"
              aria-label="Subscribe to alerts"
              borderRadius="base"
              leftIcon={<HiOutlineBell height="12px" />}
              isLoading={subscriptionLoading}
              onClick={() => {
                addSubscription({
                  variables: {
                    positionIds: [position.id],
                    candidateIds: [],
                  },
                });
              }}
            >
              Subscribe to alerts
            </Button>
          )}
        {!candidateAlertsDisabled &&
          currentAlertSubscription &&
          !currentAlertSubscription.mutedAt && (
            <Button
              colorScheme="blue"
              aria-label="Subscribe to alerts"
              borderRadius="base"
              leftIcon={<HiOutlineBellSlash height="12px" />}
              isLoading={subscriptionLoading}
              onClick={() => {
                muteSubscription({
                  variables: {
                    positionIds: [position.id],
                    candidateIds: [],
                  },
                });
              }}
            >
              Mute alerts
            </Button>
          )}
        {!!position.customAtsUrl && (
          <Button
            as="a"
            href={position.customAtsUrl}
            target="_blank"
            leftIcon={<FiExternalLink height="12px" />}
            colorScheme="blue"
            aria-label="View in your ATS"
            borderRadius="base"
          >
            ATS
          </Button>
        )}
        <Button
          onClick={() => navigate(hiringTeamLink)}
          leftIcon={<IoPeopleSharp height="12px" />}
          colorScheme="blue"
          aria-label="Lever"
          borderRadius="base"
        >
          View hiring team
        </Button>
      </ButtonGroup>
    </Flex>
  );
};

export default PositionHeader;
